/**
 * Expedição — **fila de separação → despacho** (Task 14, brief 3.7).
 *
 * Leia isto antes de mexer:
 *
 * 1. **A fila não tem máquina própria.** Etapas, próximos passos e o que cada
 *    passo exige vêm da regra pura (`src/fulfillment/state-machine.ts`); a tela
 *    só lista e navega.
 * 2. **Estado interno + execução externa.** Cada linha mostra a etapa INTERNA
 *    da expedição e o estado do TRANSPORTADOR (rastreio simulado sem
 *    credencial), lado a lado — a visão é unificada mesmo quando a execução é
 *    externa (D-14.4).
 * 3. **Ocorrência é visível.** Tarefa com ocorrência pendente (atraso, avaria,
 *    endereço, ausente, extravio) aparece marcada na fila; o recorte
 *    `?occurrences=1` lista só as exceções que precisam de ação.
 * 4. **Fonte ausente não é "nenhuma expedição".** Sem `/api/platform/fulfillment`
 *    a tela declara a ausência, em vez de uma fila vazia que leria como "nada a
 *    expedir".
 *
 * Migrado para `.tsx`/JSX real (Cluster D — Logística & Garantias, redesign
 * por perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe
 * apenas (`createElement` → JSX): a tela já estava tokenizada e já tinha o
 * ícone `TriangleAlert` no selo de ocorrência (achado da investigação inicial
 * do cluster) — nenhum ícone novo foi adicionado. Nenhum `data-*`/aria/texto/
 * rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `FulfillmentStateMachine.tsx`.
import React, { type ReactElement } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Truck, TriangleAlert } from "lucide-react";
import { StageStrip } from "../components/StageStrip.js";
import { EmptyState, PageHeader } from "../ui/index.js";
import { RecordTable } from "../components/RecordTable.js";
import { FulfillmentBoundary } from "../components/FulfillmentBoundary.js";
import { StatusPanel } from "../components/StatusPanel.js";
import {
  isFulfillmentSourceAwaitingContract,
  occurrenceBreakdown,
  stageBreakdown,
  tasksByStage,
  tasksWithOccurrence,
  unifiedView
} from "../fulfillment/dataset.js";
import {
  FULFILLMENT_STAGE_LABELS,
  OCCURRENCE_KIND_LABELS,
  isFulfillmentStage,
  type FulfillmentStage
} from "../fulfillment/state-machine.js";
import { ORDER_ORIGIN_LABELS } from "../orders/state-machine.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatDateTime } from "../format.js";
import type { FulfillmentDataset } from "../fulfillment/dataset.js";
import type { ExpedicaoViewProps } from "../fulfillment/view-props.js";
import type { FulfillmentTask } from "../api/types.js";

/** Rótulo honesto do rastreio do transportador (nunca o código cru na UI). */
const TRACKING_STATUS_LABELS: Record<string, string> = {
  confirmed: "Confirmado (aguardando postagem)",
  shipped: "Postado",
  in_transit: "Em trânsito",
  delivered: "Entregue",
  exception: "Exceção reportada"
};

function trackingLabel(status: string | null): string {
  if (status === null) return "sem evento do transportador ainda";
  return TRACKING_STATUS_LABELS[status] ?? status;
}

export function ExpedicaoFilaTable({ tasks }: { tasks: FulfillmentTask[] }): ReactElement {
  return (
    <RecordTable<FulfillmentTask>
      caption="Ordens de expedição, com etapa interna e estado do transportador"
      rows={tasks}
      idOf={(task) => task.id}
      emptyMessage="Nenhuma expedição neste recorte."
      // Fase 10 (Plano de Ataque R8): rollout mecânico do primitivo da Fase 6.
      animateRows
      columns={[
        {
          key: "id",
          label: "Expedição",
          render: (task) => <Link to={`/expedicao/${task.id}`}>{task.id}</Link>
        },
        {
          key: "order",
          label: "Pedido",
          render: (task) => (
            <span data-order-external-id={task.orderExternalId}>
              <Link to={`/pedidos/${task.platformOrderId}`} title="Abrir o pedido que esta expedição despacha">
                {task.orderExternalId}
              </Link>
            </span>
          )
        },
        {
          key: "origin",
          label: "Origem",
          render: (task) => <span data-order-origin={task.orderOrigin}>{ORDER_ORIGIN_LABELS[task.orderOrigin]}</span>
        },
        {
          key: "stage",
          label: "Etapa interna",
          render: (task) => <span data-fulfillment-stage={task.stage}>{FULFILLMENT_STAGE_LABELS[task.stage]}</span>
        },
        {
          key: "tracking",
          label: "Transportador",
          render: (task) => {
            const view = unifiedView(task);
            return (
              <span data-tracking-status={task.tracking.status ?? ""} data-fulfillment-sync={view.sync}>
                {trackingLabel(task.tracking.status)}
              </span>
            );
          }
        },
        {
          key: "occurrence",
          label: "Ocorrência",
          render: (task) =>
            task.occurrenceKind ? (
              <span
                data-occurrence-kind={task.occurrenceKind}
                className="field--pending inline-flex items-center gap-1 font-semibold text-brand-text"
              >
                <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden focusable={false} />
                <span>{OCCURRENCE_KIND_LABELS[task.occurrenceKind]}</span>
              </span>
            ) : (
              <span data-occurrence-kind="">—</span>
            )
        },
        { key: "customer", label: "Cliente", render: (task) => task.customerName ?? "—" },
        { key: "updatedAt", label: "Atualizada", render: (task) => formatDateTime(task.updatedAt) }
      ]}
    />
  );
}

/** Composição por etapa — o que a fila contém, declarado, com atalho de recorte por etapa. */
function StageSummary({ tasks, stage }: { tasks: FulfillmentDataset; stage: FulfillmentStage | null }): ReactElement {
  const rows = stageBreakdown(tasks);
  return (
    <StageStrip
      ariaLabel="Composição da fila por etapa"
      total={tasks.tasks.length}
      data-stage-summary={tasks.tasks.length}
      activeKey={stage}
      items={rows.map((row) => ({ key: row.stage, label: row.label, count: row.count, to: `/expedicao?stage=${row.stage}` }))}
    />
  );
}

/** Composição das ocorrências pendentes — a fila de exceções, declarada. */
function OccurrenceSummary({ tasks }: { tasks: FulfillmentDataset }): ReactElement {
  const rows = occurrenceBreakdown(tasks).filter((row) => row.count > 0);
  const total = tasksWithOccurrence(tasks).length;
  return (
    <p
      className={
        "m-0 flex flex-wrap items-center gap-2 rounded-control px-3 py-2 text-sm " +
        (total === 0 ? "bg-surface-soft text-muted" : "bg-warning-soft text-ink")
      }
      data-occurrence-summary={total}
    >
      {total > 0 ? <TriangleAlert className="h-4 w-4 shrink-0 text-warning" aria-hidden focusable={false} /> : null}
      {total === 0
        ? "Nenhuma ocorrência pendente na fila."
        : `Ocorrências pendentes (${total}): ${rows.map((row) => `${row.label}: ${row.count}`).join(" · ")}`}
      {total > 0 ? (
        <Link to="/expedicao?occurrences=1" className="page__note-link font-bold text-brand-text hover:underline">
          {" ver só as ocorrências"}
        </Link>
      ) : null}
    </p>
  );
}

export function ExpedicaoView({ tasks, stage = null, onlyOccurrences = false }: ExpedicaoViewProps): ReactElement {
  if (isFulfillmentSourceAwaitingContract(tasks)) {
    return (
      <div className="page page--expedicao flex flex-col gap-6" data-page="expedicao">
        <PageHeader title="Expedição" />
        <StatusPanel
          kind="awaiting_source"
          title={`Fila de expedição ${AWAITING_CONTRACT_LABEL}`}
          detail={
            "A rota /api/platform/fulfillment não respondeu neste ambiente. A fila de separação/conferência/" +
            "embalagem/despacho aparece quando a rota for publicada — exibir uma fila vazia diria, falsamente, que não há nada a expedir."
          }
        />
      </div>
    );
  }

  const filtered = onlyOccurrences
    ? tasksWithOccurrence(tasks)
    : stage
      ? tasksByStage(tasks, stage)
      : tasks.tasks;

  return (
    <div className="page page--expedicao flex flex-col gap-6" data-page="expedicao">
      <PageHeader
        title="Expedição"
        description="Fila de separação até o despacho, com a etapa interna e o estado do transportador lado a lado."
        actions={
          onlyOccurrences || stage ? (
            <Link to="/expedicao" className="text-sm font-bold text-brand-text underline">
              Ver a fila completa
            </Link>
          ) : null
        }
      />
      <p className="m-0 text-sm font-bold text-muted" data-fulfillment-count={filtered.length}>
        {onlyOccurrences
          ? `${filtered.length} expedição(ões) com ocorrência pendente.`
          : stage
            ? `${filtered.length} expedição(ões) na etapa "${FULFILLMENT_STAGE_LABELS[stage]}".`
            : `${filtered.length} expedição(ões) na fila.`}
      </p>
      {/* Resumos/avisos de escopo da fila, preservando data-stage-summary/data-occurrence-summary
          e o link de ocorrências. */}
      <section className="flex flex-col gap-3 rounded-panel border border-border bg-surface p-5 shadow-s1">
        <StageSummary tasks={tasks} stage={stage} />
        <OccurrenceSummary tasks={tasks} />
        <p className="page__note m-0 text-xs text-muted">
          A expedição não recadastra o pedido: cada ordem referencia o pedido unificado (mesmos registros, mesmo
          histórico). O estado do transportador vem do rastreio (simulado sem credencial de transportadora).
        </p>
      </section>
      {filtered.length === 0 ? (
        <EmptyState icon={Truck} title="Nenhuma expedição neste recorte." description="Quando um pedido pago entrar em separação, ele aparece aqui com a etapa interna e o transportador." />
      ) : (
        <ExpedicaoFilaTable tasks={filtered} />
      )}
    </div>
  );
}

export function Expedicao(): ReactElement {
  const [searchParams] = useSearchParams();
  const stageParam = searchParams.get("stage");
  const stage: FulfillmentStage | null = isFulfillmentStage(stageParam) ? stageParam : null;
  const onlyOccurrences = searchParams.get("occurrences") === "1";
  return (
    <FulfillmentBoundary>
      {(tasks: FulfillmentDataset) => <ExpedicaoView tasks={tasks} stage={stage} onlyOccurrences={onlyOccurrences} />}
    </FulfillmentBoundary>
  );
}
